import { useEffect, useState } from "react";
import { User, Session } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";

export const useAuth = () => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [userType, setUserType] = useState<string | null>(null);
  const navigate = useNavigate();

  const checkPremiumStatus = async (authedUser: User) => {
    try {
      const today = new Date().toISOString().split('T')[0];
      const { data: profile } = await supabase
        .from("profiles")
        .select("is_premium, premium_end_date, last_connected")
        .eq("id", authedUser.id)
        .single();

      if (!profile) return;

      const lastConnected = profile.last_connected ? new Date(profile.last_connected).toISOString().split('T')[0] : null;
      const shouldCheck = lastConnected !== today;

      if (shouldCheck && profile.is_premium) {
        if (profile.premium_end_date) {
          const endDate = new Date(profile.premium_end_date);
          const now = new Date();
          if (endDate < now) {
            await supabase.from("profiles").update({ is_premium: false, last_connected: today }).eq("id", authedUser.id);
          } else {
            await supabase.from("profiles").update({ last_connected: today }).eq("id", authedUser.id);
          }
        } else {
          await supabase.from("profiles").update({ last_connected: today }).eq("id", authedUser.id);
        }
      } else if (shouldCheck) {
        await supabase.from("profiles").update({ last_connected: today }).eq("id", authedUser.id);
      }
    } catch (error) {
      console.error("Error checking premium status:", error);
    }
  };

  const ensureProfileExists = async (authedUser: User) => {
    try {
      const { data: existing, error: selectError } = await supabase
        .from("profiles")
        .select("id")
        .eq("id", authedUser.id)
        .maybeSingle();

      if (selectError || existing?.id) return;

      const meta: any = authedUser.user_metadata || {};
      const userType = (meta.user_type === "developer" || meta.user_type === "creator" || meta.user_type === "player") ? meta.user_type : "creator";
      const displayName = typeof meta.display_name === "string" && meta.display_name.trim().length > 0
        ? meta.display_name
        : (authedUser.email ? authedUser.email.split("@")[0] : "user");

      const { error: insertError } = await supabase
        .from("profiles")
        .insert([{
          id: authedUser.id,
          user_type: userType,
          display_name: displayName,
          is_newsletter: meta.is_newsletter || false,
          coins: 0,
          trust_score: 0
        }]);

      if (!insertError) {
        supabase.functions.invoke('update-brevo-contact', {
          body: {
            email: authedUser.email,
            is_newsletter: meta.is_newsletter || false,
            user_type: userType,
            display_name: displayName
          }
        }).catch(err => console.error('Failed to sync with Brevo:', err));

        if (userType === 'player') {
          try {
            const { completeMissionByAction } = await import('@/services/pointsService');
            await completeMissionByAction(authedUser.id, 'first_signup');
            toast.success('Welcome! You received 25 coins for signing up!');
          } catch (error) {
            console.error('Error completing signup mission:', error);
          }
        }
      }
    } catch (e) {
      console.error("Error in ensureProfileExists:", e);
    }
  };

  const checkSignupMissionFallback = async (authedUser: User, type: string, currentCoins: number) => {
    if (type !== 'player' || currentCoins >= 25) return;

    const createdAt = new Date(authedUser.created_at);
    const oneDayAgo = new Date();
    oneDayAgo.setDate(oneDayAgo.getDate() - 1);

    if (createdAt > oneDayAgo) {
      try {
        const { addCoins, completeMissionByAction } = await import('@/services/pointsService');
        const success = await addCoins(authedUser.id, 25);
        if (success) {
          await completeMissionByAction(authedUser.id, 'first_signup');
          console.log('Signup mission fallback (points-based): 25 coins awarded');
          toast.success('Welcome! You received 25 coins for signing up!');
        }
      } catch (error) {
        console.error('Error in signup mission fallback:', error);
      }
    }
  };

  const initializeUserData = async (authedUser: User) => {
    try {
      await ensureProfileExists(authedUser);

      const { data: profile, error } = await supabase
        .from("profiles")
        .select("user_type, coins, is_premium, premium_end_date, last_connected")
        .eq("id", authedUser.id)
        .maybeSingle();

      if (error) {
        console.error("Error fetching profile during init:", error);
      }

      if (profile) {
        if (profile.user_type) setUserType(profile.user_type);
        void checkPremiumStatus(authedUser);
        void checkSignupMissionFallback(authedUser, profile.user_type || 'player', profile.coins || 0);
      }
    } catch (error) {
      console.error("Error initializing user data:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        setSession(session);
        setUser(session?.user ?? null);
        if (session?.user) {
          void initializeUserData(session.user);
        } else {
          setLoading(false);
          setUserType(null);
        }
      }
    );

    supabase.auth.getSession().then(async ({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      if (session?.user) {
        void initializeUserData(session.user);
      } else {
        setLoading(false);
        setUserType(null);
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  const signUp = async (email: string, password: string, userData: { user_type: string; display_name: string; is_newsletter?: boolean }, captchaToken?: string) => {
    try {
      const { error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          emailRedirectTo: `${window.location.origin}/`,
          data: userData,
          captchaToken
        }
      });
      if (error) throw error;
      toast.success("Account created successfully! Please check your email.");
      navigate("/");
    } catch (error: any) {
      console.error("Signup error", error);
      toast.error(error?.message || "Signup failed. Please try again.");
      return { error };
    }
  };

  const signIn = async (email: string, password: string) => {
    try {
      const { error } = await supabase.auth.signInWithPassword({ email, password });
      if (error) throw error;

      const { data: { session } } = await supabase.auth.getSession();
      if (session?.user) {
        await initializeUserData(session.user);
      }

      toast.success("Welcome back!");
      navigate("/dashboard");
    } catch (error: any) {
      toast.error(error.message);
      return { error };
    }
  };

  const signOut = async () => {
    try {
      const { error } = await supabase.auth.signOut();
      if (error) throw error;
      setUserType(null);
      toast.success("Signed out successfully");
      navigate("/");
    } catch (error: any) {
      toast.error(error.message);
    }
  };

  return {
    user,
    session,
    loading,
    userType,
    signUp,
    signIn,
    signOut
  };
};
